import { ReactNode, useMemo, useState } from 'react';
import { RowSkeleton } from './Skeleton';
import { EmptyState } from './EmptyState';
import { LongText } from './LongText';

export interface Column<T> {
  key: string;
  header: string;
  render: (row: T) => ReactNode;
  sortable?: boolean;
  sortValue?: (row: T) => string | number;
  /**
   * Modo excel: texto con el que se filtra esta columna (aparece un filtro bajo el encabezado).
   * Con pocos valores distintos es una lista; con muchos, un cuadro de texto. Tambien alimenta la busqueda general.
   */
  filterValue?: (row: T) => string;
}

interface DataTableProps<T> {
  columns: Column<T>[];
  rows: T[];
  rowKey: (row: T) => string;
  isLoading?: boolean;
  emptyTitle?: string;
  emptyDescription?: string;
  /** Tamano de pagina inicial. Estandar de la plataforma: 5, 10 o 15 (ver pageSizeOptions). */
  pageSize?: number;
  /** Opciones del selector de paginacion. Por defecto [5, 10, 15] (estandar LCC para toda tabla). */
  pageSizeOptions?: number[];
  defaultSortKey?: string;
  defaultSortDir?: 'asc' | 'desc';
  /** Clase(s) extra para el contenedor con scroll horizontal (ej. "scroll-accent" en Resultados Financieros). No afecta al resto de la plataforma si se omite. */
  scrollClassName?: string;
  /**
   * 'cards' (por defecto): en pantallas angostas cada fila se muestra como una tarjeta apilada
   * (sin botones encima del texto). 'scroll': siempre tabla con scroll horizontal; es lo que se usa
   * cuando se pasa scrollClassName (tablas anchas de Resultados Financieros).
   */
  mobile?: 'cards' | 'scroll';
  /**
   * Modo "excel" (opt-in; las demas pantallas no cambian): encabezado delgado, una sola linea por fila
   * (lo largo se corta con "ver"), primera columna fija al desplazarse, buscador general, filtros por columna
   * y paginacion 5/10/50/75/100/150 (10 por defecto).
   */
  excel?: boolean;
  /** Modo excel: clave de la columna que se muestra primero y queda fija (normalmente el nombre de la persona, o el identificador de la fila). */
  fixedKey?: string;
}

/** Un texto largo dentro de una celda se corta con "ver más". */
const LONG_CELL_CHARS = 80;
const EXCEL_CELL_CHARS = 40;
const EXCEL_PAGE_SIZES = [5, 10, 50, 75, 100, 150];
function cellContent(node: ReactNode, excel = false): ReactNode {
  const max = excel ? EXCEL_CELL_CHARS : LONG_CELL_CHARS;
  return typeof node === 'string' && node.length > max ? <LongText text={node} max={max} /> : node;
}

/**
 * DataTable generico y reutilizable: soporta ordenamiento y paginacion en cliente,
 * con selector de tamano de pagina (5/10/15) estandar para toda la plataforma.
 * Los modulos que requieran paginacion server-side deben pasar `rows` ya paginado.
 */
export function DataTable<T>({
  columns: columnsProp,
  rows,
  rowKey,
  isLoading,
  emptyTitle = 'Sin registros',
  emptyDescription,
  pageSize: initialPageSize,
  pageSizeOptions,
  defaultSortKey,
  defaultSortDir = 'desc',
  scrollClassName = '',
  mobile,
  excel = false,
  fixedKey,
}: DataTableProps<T>) {
  const columns = useMemo(() => {
    if (!excel || !fixedKey) return columnsProp;
    const i = columnsProp.findIndex((c) => c.key === fixedKey);
    return i <= 0 ? columnsProp : [columnsProp[i], ...columnsProp.slice(0, i), ...columnsProp.slice(i + 1)];
  }, [excel, fixedKey, columnsProp]);
  const sizes = pageSizeOptions ?? (excel ? EXCEL_PAGE_SIZES : [5, 10, 15]);
  const startSize = initialPageSize ?? (excel ? 10 : 5);
  const [search, setSearch] = useState('');
  const [colFilters, setColFilters] = useState<Record<string, string>>({});
  const mobileMode = mobile ?? (excel || scrollClassName ? 'scroll' : 'cards');
  const [sortKey, setSortKey] = useState<string | null>(defaultSortKey ?? null);
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>(defaultSortDir);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(startSize);

  const filterCols = useMemo(() => (excel ? columns.filter((c) => c.filterValue) : []), [excel, columns]);
  const options = useMemo(() => {
    const out: Record<string, string[] | null> = {};
    for (const c of filterCols) {
      const set = new Set<string>();
      for (const r of rows) { const v = (c.filterValue!(r) ?? '').trim(); if (v) set.add(v); if (set.size > 12) break; }
      out[c.key] = set.size > 12 ? null : [...set].sort((a, b) => a.localeCompare(b, 'es'));
    }
    return out;
  }, [filterCols, rows]);
  const filtered = useMemo(() => {
    if (!excel) return rows;
    const q = search.trim().toLowerCase();
    return rows.filter((r) => {
      for (const c of filterCols) {
        const f = (colFilters[c.key] ?? '').trim().toLowerCase();
        if (!f) continue;
        const v = (c.filterValue!(r) ?? '').toLowerCase();
        if (options[c.key] ? v !== f : !v.includes(f)) return false;
      }
      if (!q) return true;
      return filterCols.some((c) => (c.filterValue!(r) ?? '').toLowerCase().includes(q));
    });
  }, [excel, rows, search, colFilters, filterCols, options]);

  const sorted = useMemo(() => {
    if (!sortKey) return filtered;
    const col = columns.find((c) => c.key === sortKey);
    if (!col?.sortValue) return filtered;
    const copy = [...filtered];
    copy.sort((a, b) => {
      const av = col.sortValue!(a);
      const bv = col.sortValue!(b);
      if (av < bv) return sortDir === 'asc' ? -1 : 1;
      if (av > bv) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
    return copy;
  }, [filtered, sortKey, sortDir, columns]);

  const totalPages = Math.max(1, Math.ceil(sorted.length / pageSize));
  const currentPage = Math.min(page, totalPages);
  const paged = sorted.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  const toggleSort = (col: Column<T>) => {
    if (!col.sortable) return;
    if (sortKey === col.key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(col.key);
      setSortDir('asc');
    }
  };

  if (isLoading) {
    return (
      <div className="overflow-hidden rounded-2xl border border-ink-100 bg-surface">
        {Array.from({ length: 5 }).map((_, i) => (
          <RowSkeleton key={i} />
        ))}
      </div>
    );
  }

  const hasFilters = excel && (search.trim() !== '' || Object.values(colFilters).some((v) => v.trim() !== ''));
  if (rows.length === 0) {
    return <EmptyState title={emptyTitle} description={emptyDescription} />;
  }

  return (
    <div className="overflow-hidden rounded-2xl border border-ink-100 bg-surface">
      {excel && filterCols.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 border-b border-ink-100 px-3 py-2">
          <input
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            placeholder="Buscar en la tabla…"
            className="w-full max-w-xs rounded-lg border border-ink-200 bg-surface px-3 py-1.5 text-sm outline-none focus:ring-2 focus:ring-brand-200"
          />
          {hasFilters && (
            <button type="button" onClick={() => { setSearch(''); setColFilters({}); setPage(1); }} className="text-xs font-semibold text-brand-600 hover:underline">
              Limpiar filtros
            </button>
          )}
        </div>
      )}
      {mobileMode === 'cards' && (
        <div className="divide-y divide-ink-100 sm:hidden">
          {paged.map((row) => {
            const [titleCol, ...rest] = columns;
            const fields = rest.filter((c) => c.header !== '');
            const actions = rest.filter((c) => c.header === '');
            return (
              <div key={rowKey(row)} className="space-y-2 p-4">
                {titleCol && <div className="min-w-0 break-words text-sm font-semibold text-ink-900">{cellContent(titleCol.render(row))}</div>}
                {fields.map((col) => (
                  <div key={col.key} className="flex items-start justify-between gap-3 text-sm">
                    <span className="shrink-0 pt-0.5 text-[11px] font-semibold uppercase tracking-wide text-ink-400">{col.header}</span>
                    <div className="min-w-0 break-words text-right text-ink-700">{cellContent(col.render(row))}</div>
                  </div>
                ))}
                {actions.length > 0 && <div className="flex flex-wrap items-center justify-end gap-1 pt-1">{actions.map((col) => <div key={col.key}>{col.render(row)}</div>)}</div>}
              </div>
            );
          })}
        </div>
      )}
      <div className={[mobileMode === 'cards' ? 'hidden sm:block' : '', 'overflow-x-auto', excel ? 'excel-scroll max-h-[70vh] overflow-y-auto' : '', scrollClassName].filter(Boolean).join(' ')}>
        <table className={['w-full', excel ? 'excel-table text-[13px]' : 'text-sm'].join(' ')}>
          <thead>
            <tr className="border-b border-ink-100 bg-ink-50/60 text-left text-xs font-semibold uppercase tracking-wide text-ink-500">
              {columns.map((col, i) => (
                <th
                  key={col.key}
                  onClick={() => toggleSort(col)}
                  className={[
                    'whitespace-nowrap',
                    excel ? 'px-3 py-1.5' : 'px-4 py-3',
                    col.sortable ? 'cursor-pointer select-none' : '',
                    excel ? 'sticky top-0 z-10 bg-ink-50' : '',
                    excel && i === 0 ? 'left-0 z-20 border-r border-ink-100' : '',
                  ].join(' ')}
                >
                  {col.header}
                  {sortKey === col.key && (sortDir === 'asc' ? ' ▲' : ' ▼')}
                </th>
              ))}
            </tr>
            {excel && filterCols.length > 0 && (
              <tr className="border-b border-ink-100 bg-surface">
                {columns.map((col, i) => (
                  <th key={col.key} className={['px-2 py-1 font-normal normal-case', 'sticky top-[29px] z-10 bg-surface', i === 0 ? 'left-0 z-20 border-r border-ink-100' : ''].join(' ')}>
                    {col.filterValue && (options[col.key] ? (
                      <select value={colFilters[col.key] ?? ''} onChange={(e) => { setColFilters((f) => ({ ...f, [col.key]: e.target.value })); setPage(1); }}
                        className="w-full min-w-[6rem] rounded border border-ink-200 bg-surface px-1 py-0.5 text-xs text-ink-700 outline-none">
                        <option value="">Todos</option>
                        {options[col.key]!.map((v) => <option key={v} value={v}>{v}</option>)}
                      </select>
                    ) : (
                      <input value={colFilters[col.key] ?? ''} onChange={(e) => { setColFilters((f) => ({ ...f, [col.key]: e.target.value })); setPage(1); }}
                        placeholder="Filtrar…" className="w-full min-w-[6rem] rounded border border-ink-200 bg-surface px-1.5 py-0.5 text-xs outline-none" />
                    ))}
                  </th>
                ))}
              </tr>
            )}
          </thead>
          <tbody>
            {paged.map((row) => (
              <tr key={rowKey(row)} className="border-b border-ink-50 last:border-0 hover:bg-ink-50/40">
                {columns.map((col, i) => (
                  <td key={col.key} className={[
                    excel || scrollClassName ? 'whitespace-nowrap' : 'max-w-[22rem] break-words align-top',
                    excel ? 'px-3 py-1.5' : 'px-4 py-3',
                    'text-ink-700',
                    excel && i === 0 ? 'sticky left-0 z-[5] border-r border-ink-100 bg-surface font-medium text-ink-900' : '',
                  ].join(' ')}>
                    {cellContent(col.render(row), excel)}
                  </td>
                ))}
              </tr>
            ))}
            {paged.length === 0 && (
              <tr><td colSpan={columns.length} className="px-4 py-6 text-center text-sm text-ink-400">Ningún registro coincide con la búsqueda o los filtros.</td></tr>
            )}
          </tbody>
        </table>
      </div>
      <div className="flex flex-wrap items-center justify-between gap-3 border-t border-ink-100 px-4 py-3 text-sm text-ink-500">
        <span>
          Página {currentPage} de {totalPages} · {filtered.length}{filtered.length !== rows.length ? ` de ${rows.length}` : ''} registros
        </span>
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-1.5 text-xs">
            Ver
            <select
              value={pageSize}
              onChange={(e) => {
                setPageSize(Number(e.target.value));
                setPage(1);
              }}
              className="rounded-lg border border-ink-200 bg-surface px-2 py-1 text-xs font-medium text-ink-700 outline-none focus:ring-2 focus:ring-brand-200"
            >
              {sizes.map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </select>
            por página
          </label>
          <div className="flex gap-2">
            <button disabled={currentPage <= 1} onClick={() => setPage((p) => p - 1)} className="rounded-lg px-3 py-1.5 disabled:opacity-40 hover:bg-ink-50">
              Anterior
            </button>
            <button disabled={currentPage >= totalPages} onClick={() => setPage((p) => p + 1)} className="rounded-lg px-3 py-1.5 disabled:opacity-40 hover:bg-ink-50">
              Siguiente
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
